import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Alert,
  AlertDescription,
  Input,
} from "@repo/ui";
import { Loader2, ShieldCheck } from "lucide-react";

interface PasswordConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (password: string) => void | Promise<void>;
  title?: string;
  description?: string;
  actionLabel?: string;
}

export function PasswordConfirmationModal({
  open,
  onClose,
  onSuccess,
  title = "Confirm Your Password",
  description = "This action requires password confirmation for security and audit purposes.",
  actionLabel = "Confirm",
}: PasswordConfirmationModalProps) {
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClose = () => {
    setPassword("");
    setError("");
    setIsLoading(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!password || password.length < 4) {
      setError("Please enter your password");
      return;
    }

    setIsLoading(true);

    try {
      await onSuccess(password);
      // If successful, close and reset
      handleClose();
    } catch (err) {
      // Handle error from parent component
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Invalid password or operation failed");
      }
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          <div className="space-y-2">
            <Label htmlFor="confirm-password">
              Password <span className="text-red-500">*</span>
            </Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              disabled={isLoading}
              className={error ? "border-red-500" : ""}
              autoComplete="one-time-code"
              data-testid="password-confirm-input"
            />
            <p className="text-xs text-muted-foreground">
              This action will be logged in the audit trail
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              data-testid="password-confirm-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                actionLabel
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
